import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/** A refusal from the booking server, with its reason when it has one. */
export class PublicBookingError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = "PublicBookingError";
  }
}

/** The owner's booking rules (cut-off, upcoming limit). Their message is
 * written for the customer and can be shown as is. */
export const isBookingRuleError = (error: unknown): error is PublicBookingError =>
  error instanceof PublicBookingError &&
  (error.code === "too_late" || error.code === "too_many_upcoming");

/** Reads `{ error, code }` from a failed edge function call. */
export async function readBookingError(error: unknown, fallback: string): Promise<PublicBookingError> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json();
      if (typeof body?.error === "string") {
        return new PublicBookingError(body.error, typeof body.code === "string" ? body.code : undefined);
      }
    } catch {
      // Unreadable body: fall back below.
    }
  }
  return new PublicBookingError(fallback);
}

/** Has online booking closed for a session starting at date + time (SGT)? */
export function isPastBookingCutoff(
  date: string,
  time24: string,
  cutoffHours: number | null | undefined,
  now = Date.now(),
): boolean {
  if (!cutoffHours) return false;
  const startsAt = new Date(`${date}T${time24.slice(0, 5)}:00+08:00`).getTime();
  if (!Number.isFinite(startsAt)) return false;
  return startsAt < now + cutoffHours * 60 * 60 * 1000;
}

export interface SlotUsageRow {
  booking_date: string;
  booking_time: string;
  number_of_pax: number | null;
}

export async function getPublicSlotUsage(
  listingId: string,
  dates: string[],
  options: { excludeBooking?: string; accessToken?: string } = {},
): Promise<SlotUsageRow[]> {
  const { data, error } = await supabase.functions.invoke("public-booking", {
    body: {
      action: "slot_usage",
      listing_id: listingId,
      dates,
      exclude_booking: options.excludeBooking ?? null,
      access_token: options.accessToken ?? null,
    },
  });
  if (error || !Array.isArray(data?.slots)) {
    throw new Error("Availability is temporarily unavailable.");
  }
  return data.slots as SlotUsageRow[];
}

export async function upsertPublicCustomer(input: {
  businessId: string;
  name: string;
  email: string;
  phone?: string | null;
}): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke("public-booking", {
    body: {
      action: "customer",
      business_id: input.businessId,
      customer_name: input.name,
      customer_email: input.email,
      customer_phone: input.phone ?? null,
    },
  });
  if (error) throw new Error("Customer details could not be saved.");
  return typeof data?.customer_id === "string" ? data.customer_id : null;
}

export async function createPublicBooking(
  payload: Record<string, unknown>,
  idempotencyKey = crypto.randomUUID(),
): Promise<
  { bookingId: string; accessToken: string; seriesId?: string | null }
> {
  const { data, error } = await supabase.functions.invoke("public-booking", {
    body: { action: "create", idempotency_key: idempotencyKey, payload },
  });
  if (error) throw await readBookingError(error, "The booking could not be created.");
  if (typeof data?.booking_id !== "string" || typeof data?.access_token !== "string") {
    const message = typeof data?.error === "string"
      ? data.error
      : "The booking could not be created.";
    throw new PublicBookingError(message);
  }
  return {
    bookingId: data.booking_id,
    accessToken: data.access_token,
    seriesId: typeof data.series_id === "string" ? data.series_id : null,
  };
}

export function bookingAccessUrl(
  path: string,
  token: string,
): string {
  return `${path}?token=${encodeURIComponent(token)}`;
}
